import "./file-organizer-form.css";
import { type JSX, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import CollapsibleContainer from "@via/components/collapsible-container/collapsible-container.tsx";
import { HeadingSize } from "@via/components/page-heading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import {
  useGetSavedAmazonCredentialsList,
  useGetSavedGoogleCredentialsList,
} from "@via/pages/data/data-sources/data-source-credentials.api.tsx";
import {
  BUILT_IN_ACCESS_OPTION_ID,
  BUILT_IN_ACCESS_OPTION_LABEL,
} from "@via/pages/data/constants.ts";

export interface IFileOrganizerBackupForm {
  archiveDir?: string;
  s3ArchiveDir?: string;
  s3ArchiveCredentialId?: string | number;
  gsArchiveDir?: string;
  gsArchiveCredentialId?: string | number;
}

export default function FileOrganizerBackupForm({
  onFormChange,
}: {
  onFormChange?: (payload: IFileOrganizerBackupForm) => void;
}): JSX.Element {
  const { control, watch, setValue } = useForm<IFileOrganizerBackupForm>({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      archiveDir: "",
      s3ArchiveDir: "",
      s3ArchiveCredentialId: undefined,
      gsArchiveDir: "",
      gsArchiveCredentialId: undefined,
    },
  });

  const archiveDir = watch("archiveDir");
  const s3ArchiveDirValue = watch("s3ArchiveDir");
  const s3ArchiveCredentialId = watch("s3ArchiveCredentialId");
  const gsArchiveDirValue = watch("gsArchiveDir");
  const gsArchiveCredentialId = watch("gsArchiveCredentialId");

  // Store the latest callback in a ref to avoid dependency issues
  const onFormChangeRef = useRef(onFormChange);
  useEffect(() => {
    onFormChangeRef.current = onFormChange;
  }, [onFormChange]);

  const {
    isLoading: isSavedAmazonCredentialsLoading,
    data: savedAmazonCredentials = [],
  } = useGetSavedAmazonCredentialsList();
  const amazonCredentialList = [
    { name: BUILT_IN_ACCESS_OPTION_LABEL, id: BUILT_IN_ACCESS_OPTION_ID },
    ...savedAmazonCredentials,
  ];

  const {
    isLoading: isSavedGoogleCredentialsLoading,
    data: savedGoogleCredentials = [],
  } = useGetSavedGoogleCredentialsList();
  const googleCredentialList = [
    { name: BUILT_IN_ACCESS_OPTION_LABEL, id: BUILT_IN_ACCESS_OPTION_ID },
    ...savedGoogleCredentials,
  ];

  // Watch all form values and call onFormChange when they change
  useEffect(() => {
    if (onFormChangeRef.current) {
      onFormChangeRef.current({
        archiveDir,
        s3ArchiveDir: s3ArchiveDirValue,
        s3ArchiveCredentialId,
        gsArchiveDir: gsArchiveDirValue,
        gsArchiveCredentialId,
      });
    }
  }, [
    archiveDir,
    s3ArchiveDirValue,
    s3ArchiveCredentialId,
    gsArchiveDirValue,
    gsArchiveCredentialId,
  ]);

  useEffect(() => {
    if (s3ArchiveDirValue && !s3ArchiveCredentialId) {
      setValue("s3ArchiveCredentialId", BUILT_IN_ACCESS_OPTION_ID);
    } else if (!s3ArchiveDirValue && s3ArchiveCredentialId) {
      setValue("s3ArchiveCredentialId", undefined);
    }
  }, [s3ArchiveDirValue, s3ArchiveCredentialId, setValue]);

  useEffect(() => {
    if (gsArchiveDirValue && !gsArchiveCredentialId) {
      setValue("gsArchiveCredentialId", BUILT_IN_ACCESS_OPTION_ID);
    } else if (!gsArchiveDirValue && gsArchiveCredentialId) {
      setValue("gsArchiveCredentialId", undefined);
    }
  }, [gsArchiveDirValue, gsArchiveCredentialId, setValue]);

  return (
    <div className="collapsible-container-wrapper">
      <CollapsibleContainer
        title={"Backup to Another Location (optional)"}
        size={HeadingSize.XSmall}>
        <div
          className="advanced-options"
          style={{ transition: "width 0.2s ease" }}>
          <FormInputComponent
            control={control}
            name="archiveDir"
            label="Local Archive Directory (optional)"
            placeholder="Enter your local archive directory"
            data-testid="localArchiveInput"
          />

          <FormInputComponent
            control={control}
            name="s3ArchiveDir"
            label="Amazon S3 Backup (optional)"
            placeholder="Enter your Amazon S3 Backup"
            data-testid="awsBackupInput"
          />

          {s3ArchiveDirValue && (
            <div>
              <label className="mt-4 block text-sm font-medium leading-6 text-gray-900">
                Amazon S3 Backup Credentials
              </label>
              <div className="mt-2" data-testid="s3ArchiveCredentialIdSelector">
                <SelectComponent
                  control={control}
                  name="s3ArchiveCredentialId"
                  options={
                    amazonCredentialList?.map(({ name, id }) => ({
                      label: name,
                      value: id,
                    })) ?? []
                  }
                  isLoading={isSavedAmazonCredentialsLoading}
                  placeholder="Choose Amazon Backup Credentials"
                />
              </div>
            </div>
          )}

          <FormInputComponent
            control={control}
            name="gsArchiveDir"
            label="Google Storage Backup (optional)"
            placeholder="Enter your Google Storage Backup"
            data-testid="gcsBackupInput"
          />

          {gsArchiveDirValue && (
            <div>
              <label className="mt-4 block text-sm font-medium leading-6 text-gray-900">
                Google Cloud Backup Credentials
              </label>
              <div className="mt-2" data-testid="gsArchiveCredentialIdSelector">
                <SelectComponent
                  control={control}
                  name="gsArchiveCredentialId"
                  options={
                    googleCredentialList?.map(({ name, id }) => ({
                      label: name,
                      value: id,
                    })) ?? []
                  }
                  isLoading={isSavedGoogleCredentialsLoading}
                  placeholder="Choose Google Cloud Backup Credentials"
                />
              </div>
            </div>
          )}
        </div>
      </CollapsibleContainer>
    </div>
  );
}
